/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';
import { ReactNode, useEffect } from 'react';
import { View, ScrollView, Platform } from 'react-native';
import { WebHeaderView, WebHeaderViewProps } from '../../widgets';
import React from 'react';
import { pd } from '@airasia-phoenix/react-native/utils';
// Hydration-safe: follows the server's device hint until mounted (Phoenix's
// useDeviceInfo reads the window width, which is 0 on the server).
import { useDeviceInfo } from '@airasia-rn/ui';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import {
  webFooterMock,
  WebFooterView,
  WebFooterViewProps,
} from '../../widgets/WebFooterView';
import { FeedbackButton } from '../../components';
import LoginTooltip, {
  type LoginTooltipProps,
} from '../../components/LoginTooltip';
import {
  DividerDirection,
  DividerType,
} from '@airasia-phoenix/react-native/components/DividerView/types';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { PortalHost, PortalProvider } from '@gorhom/portal';
import { WEB_CONTENT_WIDTHS } from '../../utils/layout';

export type WebLayoutProps = {
  height?: number | 'auto' | `${number}%` | null;
  header: WebHeaderViewProps;
  footer?: WebFooterViewProps;
  children: ReactNode | ReactNode[];
  customHeader?: ReactNode | ReactNode[];
  loginTooltip?: LoginTooltipProps;
  showFooter?: boolean;
  showFeedbackButton?: boolean;
  feedbackLabel: string;
  onFeedbackPress?: () => void;
  remountOnHydration?: boolean;
  paddingBottom?: number;
  /**
   * Skip the shared WEB_CONTENT_WIDTHS cap on the centred content column, for a
   * LOB whose own content already sets and centres its width. Default false.
   */
  disableContentMaxWidth?: boolean;
};

const ContentContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (Platform.OS === 'web') {
    return (
      <div style={{ position: 'sticky', top: 0, zIndex: 10 }}>{children}</div>
    );
  }
  return <View>{children}</View>;
};

const WebLayoutView: React.FC<WebLayoutProps> = ({
  height,
  header,
  footer,
  children,
  customHeader,
  loginTooltip,
  showFooter,
  showFeedbackButton = false,
  feedbackLabel,
  onFeedbackPress,
  remountOnHydration = false,
  paddingBottom,
  disableContentMaxWidth = false,
}) => {
  const { theme } = useUnistyles();
  const [hydrated, setHydrated] = React.useState(false);
  const [isContentScrolled, setIsContentScrolled] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isWeb = Platform.OS === 'web';
  const Container: React.ElementType = isWeb ? 'div' : ScrollView;
  const { isMobileWidth } = useDeviceInfo();

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web')
      document.body.style.backgroundColor = getColor(pd.sys.colour.bg_primary);
  }, [theme.isDarkMode]);

  useEffect(() => {
    if (!isWeb) return;

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsContentScrolled(container.scrollTop > 0);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isWeb, containerRef.current]);

  // Arrays, not spreads: spreading a unistyles v3 style copies none of its
  // (non-enumerable) properties, and the server and client then disagreed on
  // whether the max-width applied.
  const contentStyle = [
    styles.content,
    disableContentMaxWidth && styles.contentUncapped,
  ];

  // key forces a client re-render to work around a unistyles issue;
  // unrelated to PortalProvider itself.
  // only in ssr app, can optout by passing remountOnHydration = false
  return (
    <PortalProvider key={remountOnHydration ? hydrated.toString() : undefined}>
      <BottomSheetModalProvider>
        <View
          pointerEvents="box-none"
          style={styles.portalContainer}
          nativeID="web-portal-host"
        >
          <PortalHost name="flights-web-portal-host" />
          <PortalHost name="hotels-web-portal-host" />
        </View>
        <Container
          {...(!isWeb && { stickyHeaderIndices: [0] })}
          {...(isWeb && { ref: containerRef })}
          // On web `Container` is a raw <div>, which only accepts a plain
          // style object — unistyles entries are for RN views (and spreading a
          // unistyles v3 style copies none of its values, only a hidden key
          // that the server and client serialise differently → hydration
          // mismatch).
          style={
            isWeb
              ? {
                  backgroundColor: getColor(pd.sys.colour.bg_primary),
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'scroll',
                  ...(isMobileWidth &&
                    paddingBottom !== undefined && { paddingBottom }),
                }
              : styles.layout
          }
        >
          <ContentContainer>
            {customHeader && customHeader}
            {!customHeader && (
              <WebHeaderView
                {...(header ?? {
                  currencyText: '',
                  languageText: '',
                  accountText: '',
                  //navigationItems: mockHeaderData,
                  darkModeToggleEnabled: false,
                })}
                {...{
                  bottomDividerView:
                    isWeb && isContentScrolled
                      ? {
                          width: '100%',
                          type: DividerType.Solid,
                          direction: DividerDirection.Horizontal,
                          color: pd.sys.colour.outline_low.toString(),
                          thickness: 1,
                          height: 1,
                        }
                      : undefined,
                }}
                loginTooltip={
                  isWeb && hydrated && loginTooltip ? (
                    <LoginTooltip {...loginTooltip} />
                  ) : undefined
                }
              />
            )}
          </ContentContainer>
          <View
            style={[
              contentStyle,
              isWeb && {
                width: '100%',
                flexGrow: 1,
                height: height, // let the content set the height, previously it was 100% but it did not look greate
              },
            ]}
          >
            {children}
          </View>
          {showFooter && <WebFooterView {...(footer || webFooterMock)} />}
        </Container>
        {isWeb && showFeedbackButton && (
          <FeedbackButton
            label={feedbackLabel}
            onPress={() => {
              onFeedbackPress?.();
            }}
          />
        )}
      </BottomSheetModalProvider>
    </PortalProvider>
  );
};

export default WebLayoutView;

const styles = StyleSheet.create((pd) => ({
  layout: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    position: 'relative',
  },
  content: {
    marginHorizontal: 'auto',
    maxWidth: WEB_CONTENT_WIDTHS,
    // No zIndex here. The page content must stay at the default stacking
    // level (RNW Views are z-index 0) so the fixed portal layer above and the
    // bottom-sheet host that BottomSheetModalProvider renders after the
    // content both paint on top of it. A `zIndex: 1` used to sit here; it was
    // never applied on unistyles v2 web, and on v3 it lifted the whole page
    // above every sheet and dropdown.
  },
  contentUncapped: {
    // CSS `none` (web only): a later `maxWidth: undefined` would not override
    // the breakpoint values above.
    maxWidth: 'none' as unknown as number,
  },
  portalContainer: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: Platform.OS === 'web' ? 0 : undefined,
    zIndex: 1,
  },
}));
